import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

/**
 * Read-only public aggregates used by the Showcase Mundial surface.
 * No middleware → safe to call from public routes and SSR.
 * Uses supabaseAdmin scoped to safe public columns only.
 */

export const getTopViralClips = createServerFn({ method: "GET" }).handler(async () => {
  const since = new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString();
  const { data, error } = await supabaseAdmin
    .from("viral_clips")
    .select("id, home_team, away_team, score, minute, emotion, platform, views, shares, clicks, created_at, user_id")
    .gte("created_at", since)
    .order("shares", { ascending: false })
    .order("views", { ascending: false })
    .limit(5);
  if (error) return { rows: [], error: error.message };
  return { rows: data ?? [], error: null };
});

export const getTopCreators = createServerFn({ method: "GET" }).handler(async () => {
  const since = new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString();
  const { data: clips } = await supabaseAdmin
    .from("viral_clips")
    .select("user_id, views, shares, clicks")
    .gte("created_at", since)
    .limit(500);
  const agg = new Map<string, { clips: number; views: number; shares: number; clicks: number }>();
  for (const c of clips ?? []) {
    if (!c.user_id) continue;
    const prev = agg.get(c.user_id) ?? { clips: 0, views: 0, shares: 0, clicks: 0 };
    prev.clips += 1;
    prev.views += c.views ?? 0;
    prev.shares += c.shares ?? 0;
    prev.clicks += c.clicks ?? 0;
    agg.set(c.user_id, prev);
  }
  const ids = Array.from(agg.keys());
  let nameMap = new Map<string, { display_name: string | null; avatar_url: string | null }>();
  if (ids.length) {
    const { data: profs } = await supabaseAdmin
      .from("profiles")
      .select("id, display_name, avatar_url")
      .in("id", ids);
    nameMap = new Map((profs ?? []).map((p) => [p.id, { display_name: p.display_name, avatar_url: p.avatar_url }]));
  }
  const rows = Array.from(agg.entries())
    .map(([user_id, v]) => ({
      user_id,
      display_name: nameMap.get(user_id)?.display_name ?? "Fan",
      avatar_url: nameMap.get(user_id)?.avatar_url ?? null,
      ...v,
      score: v.shares * 3 + v.clicks * 2 + v.views,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
  return { rows };
});